import React from "react";
import { Field, Heading, List, Paragraph } from "../core/pdf/types";
import { useDesigner } from "./useDesigner";
import { ElementContext, useElementContext } from "./useElementContext";
import { getElementID, useContentState } from "./useContentState";
import { DesignerMessages } from "./types";
import { setCursorToEnd, simplifyHtmlIntoSegments } from "../core/utils/html";
import { EditableRegion } from "../core/form/EditableRegion";
import { useList } from "./useList";
import { useListItem } from "./useListItem";
import { newID } from "../core/utils/unique";
import { useTableCell } from "./useTableCell";
import { useTable } from "./useTable";
import { closest, closestKeyAsInt } from "../core/utils/dom";
import { Icon } from "../core/ui/icons";

export function ParagraphHeadingEditor(props: {
	paragraph: Paragraph | Heading,
}) {

	const designer = useDesigner();

	const list = useList();
	const listItem = useListItem();

	const tableCell = useTableCell();
	const table = useTable();

	const elementContext = useElementContext(props.paragraph);

	const state = useContentState(elementContext, React.useCallback((msg: any) => {

		if (msg.type === DesignerMessages.GET_SEGMENTS) {
			const el = document.getElementById(getElementID(props.paragraph.id!));
			if (el) {
				msg.segments = simplifyHtmlIntoSegments(el);
			}
		
		} else if (msg.type === DesignerMessages.CHANGED) {
			handleChange({
				target: {
					value: document.getElementById(state.elementID)?.innerHTML,
				}
			});

		}

	}, [props.paragraph.id]));

	React.useLayoutEffect(() => {
		// check if the designer thinks we should have focus but we don't yet
		if (state.focused) {
			const el = document.getElementById(state.elementID);

			const isWithinInspector = closest(document.activeElement, ".inspector");

			if (el && (document.activeElement !== el) && !isWithinInspector) {
				setCursorToEnd(el);

				// NOTE: resend a "focus refresh" event so the Toolbar can update now we have full context of this item
				// this is because when it initially is aware of the focus change the whole context isn't yet available for this element
				designer.broadcast({
					type: DesignerMessages.FOCUS_REFRESH,
				});
			}
		}
	});

	const style = designer.createStyle(props.paragraph);

	if (props.paragraph.type === "heading") {
		// formats for heading
		const h = props.paragraph as Heading;
		const hDef = designer.structure.defaults.headings[h.level];
		designer.updateStyleFont(style, hDef);
		if (hDef.margin) designer.updateStyleMargin(style, hDef.margin);
	}
	
	function newList(style: string) {
		// delete this paragraph and create a new list item in its place
		const parent = elementContext.parent.def as any;
		const list = designer.createItem("list") as List;
		
		if (style.startsWith("1.")) {
			list.style = "1.";
		} else if (style.startsWith("*")) {
			list.style = "o";
		}

		const i = parent.content.indexOf(elementContext.def);
		parent.content.splice(i, 1, list);

		const focus = list.items[0].content[0];
		designer.setFocus(focus);
		designer.refreshItem(parent);
	}

	function handleChange(e: any) {
		props.paragraph.html = e.target.value;

		if (props.paragraph.html && props.paragraph.html!.indexOf("\n") >= 0) {
			props.paragraph.html = props.paragraph.html.split("\n").join(" ").trim();
		}
		console.log(props.paragraph.html);

		// look out for --- to make a horiz rule
		if (props.paragraph.html === "---") {
			const parent = elementContext.parent.def as any;
			const i = parent.content.indexOf(elementContext.def);
			const hr = designer.createItem("hr");
			parent.content.splice(i, 1, hr);
			designer.setFocus(hr);
			designer.refreshItem(parent);
		}

		// lookout for start of new list
		if (props.paragraph.html === "1.&nbsp;") {
			newList(props.paragraph.html);
		} else if (props.paragraph.html === "*&nbsp;") {
			newList(props.paragraph.html);
		}

	}

	return (
		<ElementContext.Provider value={elementContext}>
			<EditableRegion
				className={"content-element " + state.classes}
				data-key={props.paragraph.id}
				id={state.elementID}

				style={style}

				intialHtml={props.paragraph.html}
				onChange={handleChange}

				onKeyDown={(e) => {
					if (e.code === "Enter") {
						// are we in a list item context?
						if (list && listItem) {
							if (e.shiftKey) {
								designer.addParagraph();
								e.preventDefault();
								return false;
							} else {	
								list.addItem(listItem.item);
								e.preventDefault();
								return false;
							}

						} else {
							designer.addParagraph();
							e.preventDefault();
							return false;

						}
					}

					if (e.ctrlKey && e.altKey) {
						if (e.keyCode >= 49 && e.keyCode <= 54) {
							// make heading
							const h = props.paragraph as Heading;
							const level = e.keyCode - 48;

							if (props.paragraph.type !== "heading" || h.level !== level) {
								h.type = "heading";
								h.level = level;
								state.refresh(newID());
							}
							e.preventDefault();
							return false;
						}
					}

					if (e.ctrlKey && e.shiftKey) {
						if (e.keyCode === 78) {
							// make normal paragraph
							if (props.paragraph.type !== "p") {
								props.paragraph.type = "p";
								const h = props.paragraph as any;
								delete h.level;
								state.refresh(newID());
							}
							e.preventDefault();
							return false;
						}
					}

					if (e.code === "Backspace") {
						const html = props.paragraph.html;
						if (!html || html === "<br>") {
							// delete this element
							designer.deleteItem(props.paragraph);
							e.preventDefault();
							return false;
						}
					}

					if (table && tableCell && e.altKey) {
						if (e.keyCode >= 37 && e.keyCode <= 40) {
							if (e.keyCode === 40) { // down arrow
								table.addRowBelow(tableCell.cell);

							} else if (e.keyCode === 38) { // up arrow
								table.addRowAbove(tableCell.cell);

							} else if (e.keyCode === 37) { // left arrow
								table.addColBefore(tableCell.cell);

							} else if (e.keyCode === 39) { // right arrow
								table.addColAfter(tableCell.cell);

							}

							e.preventDefault();
							return false;
						}
					}

					return true;
				}}

				onClick={(e) => {
					const field = closest(e.target, ".field-token") as HTMLElement;
					if (!field) return;

					const tmp = field.getAttribute("data-options");
					const fieldOptions = JSON.parse(decodeURIComponent(tmp ? tmp : ""));
					
					designer.broadcast({
						type: DesignerMessages.EDIT_FIELD,
						fieldOptions,
						elementContext,
						field,
					});

					e.preventDefault();
				}}

				onFocus={(e) => {
					// if we have received focus but aren't the focused item in designer then update
					if (designer.focus !== props.paragraph) {
						designer.setFocus(props.paragraph);
					}
				}}
			/>
		</ElementContext.Provider>
	)
}
